import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation } from "@apollo/client/react";
import { FORGOT_PASSWORD } from "../graphql/mutation";
import CustomSnackbar from "../components/Snackbar";
import {
  ForgotPasswordResponse,
  ForgotPasswordVariables,
  SnackbarType,
} from "../interfaces/auth.interface";
import { MESSAGES, REGEX } from "../utils/const";
import themestyle from "../theme";
import Copyright from "../components/Copyright";
import { PAGE_TITLES } from "../utils/const";

const FogotPassword: React.FC = () => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] =
    useState<SnackbarType>("error");
  const [forgotPassword, { loading }] = useMutation<
    ForgotPasswordResponse,
    ForgotPasswordVariables
  >(FORGOT_PASSWORD);

  useEffect(() => {
    document.title = `${PAGE_TITLES.FORGOT_PASSWORD} | United Grooves Admin Panel`;
  }, []);

  const validateEmail = () => {
    if (!email) {
      setEmailError(MESSAGES.EMAIL_REQUIRED);
      return false;
    }
    if (!REGEX.EMAIL.test(email)) {
      setEmailError(MESSAGES.EMAIL_VALID);
      return false;
    }
    setEmailError("");
    return true;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (emailError) setEmailError("");
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const isFormValid = email && !emailError;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEmail()) return;
    try {
      const { data } = await forgotPassword({
        variables: { email },
      });
      if (data?.forgotPassword?.success) {
        setSnackbarSeverity("success");
        setSnackbarMessage(data.forgotPassword.message);
        setSnackbarOpen(true);
        setTimeout(() => {
          window.location.href = "/";
        }, 1500);
      } else {
        setSnackbarSeverity("error");
        setSnackbarMessage(data?.forgotPassword?.message || MESSAGES.EMAIL_NOT_REGISTERED);
        setSnackbarOpen(true);
      }
    } catch (error: any) {
      console.error(error);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 4, sm: 3, md: 6 },
          bgcolor: themestyle.colors.white,
          position: "relative",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 420 }}>
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              gap: 1.2,
              mb: 3,
            }}
          >
            <Box
              component="img"
              src="united-grooves-logo.png"
              alt="United Grooves"
              sx={{ width: 22, height: 31 }}
            />
            <Typography
              sx={{
                fontFamily: themestyle.fontFamily.Roboto,
                fontWeight: 500,
                fontSize: "16px",
                letterSpacing: "1px",
                mt: "3px",
                ml: "4px",
              }}
            >
              {MESSAGES.BRAND_NAME}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontFamily: themestyle.fontFamily.Roboto,
              fontWeight: 500,
              fontSize: { xs: "30px", sm: "2.2rem", md: "3rem" },
              mb: 1,
              ml: { xs: 0, sm: -1, md: -1 },
            }}
          >
            {MESSAGES.FORGOT_PASSWORD_TITLE}
          </Typography>
          <Typography
            sx={{
              color: themestyle.colors.textSecondary,
              fontFamily: themestyle.fontFamily.Roboto,
              fontSize: { xs: "10px", sm: "12px", md: "14px" },
              fontWeight: 400,
              mb: 4,
              pr: 1,
              ml: { xs: 0, sm: -1, md: -1 },
            }}
          >
            {MESSAGES.FORGOT_PASSWORD_SUBTITLE}
          </Typography>
          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 1.5 }}>
              <Typography
                sx={{
                  mb: 0.8,
                  fontSize: { xs: "10px", sm: "0.9rem" },
                  fontWeight: 600,
                }}
              >
                {MESSAGES.EMAIL_LABEL}
              </Typography>
              <TextField
                fullWidth
                type="email"
                placeholder={MESSAGES.EMAIL_PLACEHOLDER}
                value={email}
                onChange={handleEmailChange}
                onBlur={validateEmail}
                error={!!emailError}
                helperText={emailError}
                FormHelperTextProps={{
                  sx: {
                    fontSize: { xs: "9px", sm: "0.75rem" },
                    marginTop: "4px",
                    marginLeft: "2px",
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    height: { xs: "40px", sm: "46px" },
                    "& input": {
                      fontSize: { xs: "10px", sm: "14px" },
                      padding: { xs: "8px 12px", sm: "10px 12px" },
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: themestyle.colors.activeGreen,
                      borderWidth: "1px",
                    },
                  },
                }}
              />
            </Box>
            <Button
              type="submit"
              fullWidth
              disabled={!isFormValid || loading}
              sx={{
                width: "100%",
                mt: { xs: 0.8, md: 2 },
                backgroundColor: themestyle.colors.activeGreen,
                color: themestyle.colors.white,
                fontSize: { xs: "11px", sm: "16px" },
                py: 1.2,
                height: { xs: "38px", sm: "auto" },
                textTransform: "none",
                borderRadius: "5px",
                "&.Mui-disabled": {
                  backgroundColor: themestyle.colors.GRAY_DISABLED,
                  color: themestyle.colors.white,
                },
                "&:hover": {
                  backgroundColor: themestyle.colors.secondary,
                },
              }}
            >
              {MESSAGES.RESET_PASSWORD_BUTTON}
            </Button>
          </form>
          <Box
            sx={{
              mt: { xs: 1.2, sm: 1.8, md: 2.3 },
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                fontFamily:themestyle.fontFamily.Roboto,
                fontSize: { xs: "10px", sm: "16px" },
                color: themestyle.colors.textSecondary,
                fontWeight:400
              }}
            >
              {MESSAGES.ALREADY_HAVE_ACCOUNT}{" "}
              <Typography
                component="span"
                sx={{
                  fontSize: { xs: "10px", sm: "16px" },
                  fontFamily:themestyle.fontFamily.Roboto,
                  color: themestyle.colors.activeGreen,
                   fontWeight:400,
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
                onClick={() => window.location.href = "/"}
              >
                {MESSAGES.SIGN_IN}
              </Typography>
            </Typography>
          </Box>
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            <Copyright />
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          display: { xs: "none", md: "flex" },
          bgcolor: themestyle.colors.black,
          position: "relative",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Copyright />
        <Box textAlign="center">
          <Box
            component="img"
            src="united-grooves-logo.png"
            alt="United Grooves"
            sx={{ width: { xs: 80, md: 200 }, mb: 2 }}
          />
          <Typography
            sx={{
              color: themestyle.colors.white,
              fontSize: { xs: 18, md: 40 },
              letterSpacing: { xs: "2px", md: "12px" },
            }}
          >
            {MESSAGES.BRAND_NAME}
          </Typography>
        </Box>
      </Box>
      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        type={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
    </Box>
  );
};
export default FogotPassword;